import { clsx } from "clsx";
import { type CSSProperties, type Key, type ReactElement, type ReactNode, useMemo } from "react";
import type { AriaTabPanelProps } from "react-aria";
import type { TabListState } from "react-stately";

import { useTabPanel } from "../../hooks/useTabPanel.ts";
import type { TabItemProps } from "./TabItem.tsx";
import style from "./Tabs.module.css";

export interface PanelTab<P = Record<string, unknown>> {
  children: ReactNode;
  key?: Key;
  itemProps?: ReactElement<P>["props"];
}

interface TabPanelRefresh<P> extends Partial<AriaTabPanelProps> {
  state: TabListState<ReactElement>;
  className?: string;
  style?: CSSProperties;
  keepAlive?: false;
  children: (tab: PanelTab<P>) => ReactNode;
}

interface TabPanelAlive<P> extends Partial<AriaTabPanelProps> {
  state: TabListState<ReactElement>;
  className?: string;
  style?: CSSProperties;
  /** ! Use only if really needed. */
  keepAlive: true;
  children: (tabs: PanelTab<P>[]) => ReactElement<{ key: Key }>[];
  childrenClassName?: string;
}

/**
 * Render a tab panel that re-renders itself with the tab content
 */
function TabPanel<P = TabItemProps>(props: TabPanelRefresh<P>): ReactElement;
function TabPanel<P = TabItemProps>(props: TabPanelAlive<P>): ReactElement;
function TabPanel<P = TabItemProps>({
  state,
  keepAlive,
  children,
  ...props
}: TabPanelAlive<P> | TabPanelRefresh<P>): ReactElement {
  const { ref, tabPanelProps } = useTabPanel<HTMLDivElement>(state, keepAlive);
  const tabCollection = useMemo<PanelTab<P>[]>(
    () =>
      [...state.collection].map((item) => ({ children: item.props.children, key: item.key, itemProps: item.props })),
    [state.collection],
  );

  return (
    <div
      {...tabPanelProps}
      ref={ref}
      // className={clsx({ [style.scroll_container]: keepAlive !== true, [style.keep_and_scroll]: keepAlive === true })}
      className={props.className}
      style={props.style}
    >
      {keepAlive
        ? children?.(tabCollection).map((cmpt) => (
            <div
              aria-hidden={state.selectedKey !== cmpt.key}
              // FIXME: childrenClassName is found nowhere?
              className={clsx((props as TabPanelAlive<P>).childrenClassName, (props as TabPanelAlive<P>).className, {
                [style.hidden_container]: state.selectedKey !== cmpt.key,
              })}
              key={`${cmpt.key}-container`}
            >
              {cmpt}
            </div>
          ))
        : children?.({
            children: state.selectedItem?.props.children,
            key: state.selectedKey ?? undefined,
            itemProps: state.selectedItem?.props,
          })}
    </div>
  );
}

export { TabPanel };
